import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
	PREVIEW_MAX_BYTES,
	PREVIEW_MAX_COLS,
	PREVIEW_MAX_ROWS,
	SpreadsheetPreviewError,
	loadSpreadsheetPreview,
	spreadsheetKind,
	type SpreadsheetPreview,
} from "@/lib/spreadsheetPreview";

interface Props {
	attachment: { url: string; name?: string; mimeType?: string };
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

function colLabel(i: number): string {
	let s = "";
	for (let n = i; n >= 0; n = Math.floor(n / 26) - 1) s = String.fromCharCode(65 + (n % 26)) + s;
	return s;
}

export function SpreadsheetPreviewDialog({ attachment, open, onOpenChange }: Props) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [preview, setPreview] = useState<SpreadsheetPreview | null>(null);
	const [active, setActive] = useState(0);

	useEffect(() => {
		if (!open) return;
		const kind = spreadsheetKind(attachment);
		if (!kind) {
			setError("Este tipo de arquivo não tem visualização.");
			return;
		}
		const ctrl = new AbortController();
		setLoading(true);
		setError(null);
		setPreview(null);
		setActive(0);
		(async () => {
			try {
				const res = await fetch(attachment.url, { signal: ctrl.signal });
				if (!res.ok) throw new SpreadsheetPreviewError("Não foi possível abrir o arquivo (sem acesso ou não encontrado).");
				const len = Number(res.headers.get("content-length") || 0);
				if (len > PREVIEW_MAX_BYTES) {
					throw new SpreadsheetPreviewError(`O arquivo passa do limite de ${PREVIEW_MAX_BYTES / (1024 * 1024)} MB para visualização.`);
				}
				const buf = await res.arrayBuffer();
				setPreview(await loadSpreadsheetPreview(buf, kind, attachment.name));
			} catch (e) {
				if (ctrl.signal.aborted) return;
				setError(e instanceof SpreadsheetPreviewError ? e.message : "Não foi possível carregar o arquivo para visualização.");
			} finally {
				if (!ctrl.signal.aborted) setLoading(false);
			}
		})();
		return () => ctrl.abort();
	}, [open, attachment.url, attachment.name, attachment.mimeType]);

	const sheet = preview?.sheets[active];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-3">
				<DialogHeader>
					<DialogTitle className="truncate pr-6">{attachment.name || "Planilha"}</DialogTitle>
					<DialogDescription>Pré-visualização somente leitura (valores das células).</DialogDescription>
				</DialogHeader>

				{loading && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" /> Carregando planilha…
					</div>
				)}

				{error && (
					<div role="alert" className="space-y-3 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
						<p>{error}</p>
						<Button asChild size="sm" variant="outline">
							<a href={attachment.url} download target="_blank" rel="noreferrer">
								Baixar
							</a>
						</Button>
					</div>
				)}

				{preview && sheet && (
					<>
						{preview.sheets.length > 1 && (
							<div role="tablist" className="flex gap-1 overflow-x-auto border-b pb-1">
								{preview.sheets.map((s, i) => (
									<button
										key={`${s.name}-${i}`}
										type="button"
										role="tab"
										aria-selected={i === active}
										onClick={() => setActive(i)}
										className={`shrink-0 rounded-t px-3 py-1 text-xs ${i === active ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent"}`}
									>
										{s.name}
									</button>
								))}
							</div>
						)}
						{(sheet.truncatedRows || sheet.truncatedCols || preview.sheetsTruncated) && (
							<p className="text-xs text-amber-600">
								{sheet.truncatedRows && `Mostrando as primeiras ${PREVIEW_MAX_ROWS} de ${sheet.totalRows} linhas. `}
								{sheet.truncatedCols && `Mostrando as primeiras ${PREVIEW_MAX_COLS} de ${sheet.totalCols} colunas. `}
								{preview.sheetsTruncated && "Algumas abas não são exibidas. "}
								Baixe o arquivo para ver tudo.
							</p>
						)}
						{sheet.rows.length === 0 ? (
							<p className="py-6 text-center text-sm text-muted-foreground">Esta aba está vazia.</p>
						) : (
							<div className="min-h-0 flex-1 overflow-auto rounded border">
								<table className="min-w-full border-collapse text-xs">
									<thead className="sticky top-0 bg-muted">
										<tr>
											<th className="border px-2 py-1" />
											{sheet.rows[0].map((_, c) => (
												<th key={c} className="border px-2 py-1 font-medium text-muted-foreground">
													{colLabel(c)}
												</th>
											))}
										</tr>
									</thead>
									<tbody>
										{sheet.rows.map((r, ri) => (
											<tr key={ri}>
												<td className="border bg-muted px-2 py-1 text-muted-foreground">{ri + 1}</td>
												{r.map((cell, ci) => (
													<td key={ci} className="max-w-[320px] truncate whitespace-nowrap border px-2 py-1" title={cell}>
														{cell}
													</td>
												))}
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

/** Botões "Visualizar" e "Baixar" para anexos de planilha. */
export function SpreadsheetActions({ attachment, className }: { attachment: { url: string; name?: string; mimeType?: string }; className?: string }) {
	const [open, setOpen] = useState(false);
	return (
		<div className={className ?? "mt-1 flex gap-2"}>
			<Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
				Visualizar
			</Button>
			<Button asChild size="sm" variant="outline">
				<a href={attachment.url} download={attachment.name || true} target="_blank" rel="noreferrer">
					Baixar
				</a>
			</Button>
			{open && <SpreadsheetPreviewDialog attachment={attachment} open={open} onOpenChange={setOpen} />}
		</div>
	);
}
